import React, { useRef, useEffect, useState } from 'react';
import { Terminal, RefreshCw, XCircle, AlertTriangle, Play, Power, Server, Plus, Trash2, ChevronDown, Radio, Wifi, Info, Loader2 } from 'lucide-react';
import { ConnectionState, type LogEntry, type ServerConfig } from '../types';
import { CONNECTION_STATE_INFO, DEFAULT_SERVERS, STORAGE_KEYS, RECONNECT_STRATEGY } from '../constants';
import type { RelayConfig } from '../core/SignalingService';
import IceRacingPanel from './IceRacingPanel';
import { decryptPeer, isEncryptedPeer } from '../core/CryptoUtils';
import { eventBus } from '../core/EventBus';

// Connection mode type
type ConnectionMode = 'direct' | 'relay';

interface ConnectionOverlayProps {
  state: ConnectionState;
  logs: LogEntry[];
  reconnectAttempt?: number;
  maxAttempts?: number;
  hostWaitingInfo?: { attempt: number; maxAttempts: number } | null;
  /** 信令重连阈值后的柔和副文案：null 表示不显示；数字为已尝试次数 */
  signalingRetryAttempt?: number | null;
  onConnectRelay: (config: RelayConfig) => void;
  onCancel: () => void;
}

// 不再读取 localStorage：每次连接都使用默认服务器列表
const loadServers = (): ServerConfig[] => [...DEFAULT_SERVERS];

// 不再读取 localStorage：每次默认选中第一个服务器
const loadLastServer = (servers: ServerConfig[]): ServerConfig => servers[0];



// 不再读取 localStorage：默认 direct 模式
const loadConnectionMode = (): ConnectionMode => 'direct';
// Relay config storage keys
const RELAY_STORAGE_KEYS = {
  SERVER_URL: 'webrtcgo_relay_server',
  PEER_ID: 'webrtcgo_relay_peer_id',
  MODE: 'webrtcgo_connection_mode',
  HISTORY: 'webrtcgo_relay_history',
};

// Default relay server (auto-detect protocol based on page protocol)
// Port 80 for ws://, port 443 for wss:// (default ports can be omitted)
const DEFAULT_RELAY_SERVER = (() => {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//webrtc.03kan.com/ws`;
})();

// Relay history item
interface RelayHistoryItem {
  id: string;
  serverUrl: string;
  peerId: string;
  name: string;
  lastUsed: number;
}

// 不再读取 localStorage：每次连接都使用默认 relay 配置
const loadRelayConfig = (): { serverUrl: string; peerId: string } => ({
  serverUrl: DEFAULT_RELAY_SERVER,
  peerId: '',
});

// 不再读取 localStorage：每次启动 relay 历史下拉为空（仅本会话内有效）
const loadRelayHistory = (): RelayHistoryItem[] => [];

// 仅在内存中维护本次会话的 relay 历史，刷新页面即清空
// history 参数由调用方传入（通常是当前 React state），避免依赖 localStorage
const addToRelayHistory = (
  serverUrl: string,
  peerId: string,
  history: RelayHistoryItem[],
): RelayHistoryItem[] => {
  const id = `${serverUrl}_${peerId}`;
  const filtered = history.filter(h => h.id !== id);
  const newItem: RelayHistoryItem = {
    id,
    serverUrl,
    peerId,
    name: peerId,
    lastUsed: Date.now(),
  };
  return [newItem, ...filtered].slice(0, 10);
};


 
// Check URL params for relay mode
// Supported: peer, Peer, peerId + optional relay server
// Note: peer value is now encrypted, needs decryption with browser fingerprint
const checkUrlParams = (): { encryptedPeer?: string; relayServer?: string } => {
  const params = new URLSearchParams(window.location.search);
  // URLSearchParams decodes '+' as space, need to restore it for Base64
  const rawPeer = params.get('peer') || params.get('Peer') || params.get('peerId') || undefined;
  return {
    encryptedPeer: rawPeer?.replace(/ /g, '+'),
    relayServer: params.get('relay') || undefined,
  };
};

const ConnectionOverlay: React.FC<ConnectionOverlayProps> = ({
  state,
  logs,
  reconnectAttempt = 0,
  maxAttempts = RECONNECT_STRATEGY.maxAttempts,
  hostWaitingInfo,
  signalingRetryAttempt = null,
  onConnectRelay,
  onCancel,
}) => {
  const logEndRef = useRef<HTMLDivElement>(null);
  const [showLogs, setShowLogs] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showRacingDetails, setShowRacingDetails] = useState(false);

  // 断线重连：P2P 失败时 WebRTCService 会冻结最后一帧做遮罩背景（无黑屏）
  const [frozenFrameUrl, setFrozenFrameUrl] = useState<string | null>(null);
  useEffect(() => {
    const onFrame = (data: { dataUrl: string | null }) => setFrozenFrameUrl(data.dataUrl);
    eventBus.on('connection:frozenFrame', onFrame);
    return () => { eventBus.off('connection:frozenFrame', onFrame); };
  }, []);
  // 恢复到 CONNECTED 或用户主动 DISCONNECT 时清除冻结帧
  useEffect(() => {
    if (state === ConnectionState.CONNECTED || state === ConnectionState.DISCONNECTED) {
      setFrozenFrameUrl(null);
    }
  }, [state]);

  // Decryption state for encrypted peer from URL
  const [isDecrypting, setIsDecrypting] = useState(false);
  const [decryptedPeerId, setDecryptedPeerId] = useState<string | null>(null);
  const [decryptError, setDecryptError] = useState<string | null>(null);

  // Connection mode state
  const [connectionMode, setConnectionMode] = useState<ConnectionMode>(() => {
    const urlParams = checkUrlParams();
    if (urlParams.encryptedPeer) return 'relay';
    return loadConnectionMode();
  });

  // Server selection state (direct mode)
  const [servers, setServers] = useState<ServerConfig[]>(() => loadServers());
  const [selectedServer, setSelectedServer] = useState<ServerConfig>(() => loadLastServer(loadServers()));
  const [showServerDropdown, setShowServerDropdown] = useState(false);
  const [showAddServer, setShowAddServer] = useState(false);
  const [newServerHost, setNewServerHost] = useState('');
  const [newServerPort, setNewServerPort] = useState('8080');
  const [newServerName, setNewServerName] = useState('');

  // Relay mode state
  const [relayServerUrl, setRelayServerUrl] = useState(() => {
    const urlParams = checkUrlParams();
    if (urlParams.relayServer) return urlParams.relayServer;
    return loadRelayConfig().serverUrl;
  });
  const [relayPeerId, setRelayPeerId] = useState(() => {
    // Don't initialize from URL here - will be set after decryption
    return loadRelayConfig().peerId;
  });
  const [relayHistory, setRelayHistory] = useState<RelayHistoryItem[]>(() => loadRelayHistory());
  const [showRelayDropdown, setShowRelayDropdown] = useState(false);

  // Decrypt peer from URL on mount (or use directly if not encrypted)
  const decryptionAttemptedRef = useRef(false);
  useEffect(() => {
    const urlParams = checkUrlParams();
    if (urlParams.encryptedPeer && !decryptionAttemptedRef.current) {
      decryptionAttemptedRef.current = true;

      // 检查是否是加密格式
      if (isEncryptedPeer(urlParams.encryptedPeer)) {
        // 加密格式，需要解密
        setIsDecrypting(true);
        setDecryptError(null);

        decryptPeer(urlParams.encryptedPeer)
          .then((peerId) => {
            setDecryptedPeerId(peerId);
            setRelayPeerId(peerId);
            setIsDecrypting(false);
          })
          .catch(() => {
            setDecryptError('无法解密连接参数，请确认链接是否正确');
            setIsDecrypting(false);
          });
      } else {
        // 未加密，直接使用
        setDecryptedPeerId(urlParams.encryptedPeer);
        setRelayPeerId(urlParams.encryptedPeer);
      }
    }
  }, []);

  // Auto-connect after successful decryption
  const autoConnectedRef = useRef(false);
  useEffect(() => {
    const urlParams = checkUrlParams();
    // Only auto-connect if we have a decrypted peer ID from URL
    if (decryptedPeerId && !autoConnectedRef.current && state === ConnectionState.DISCONNECTED && onConnectRelay) {
      autoConnectedRef.current = true;
      // Small delay to ensure component is ready
      setTimeout(() => {
        const serverUrl = urlParams.relayServer || relayServerUrl;
        onConnectRelay({ serverUrl, peerId: decryptedPeerId });
      }, 100);
    }
  }, [decryptedPeerId, state, onConnectRelay, relayServerUrl]);

  // Auto-scroll logs
  useEffect(() => {
    if (showLogs) {
      logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs, showLogs]);

  // Handle relay connect
  const handleRelayConnect = () => {
    if (!relayPeerId.trim()) return;
    // Add to history（仅本会话内存）
    setRelayHistory(prev => addToRelayHistory(relayServerUrl, relayPeerId, prev));
    onConnectRelay({ serverUrl: relayServerUrl, peerId: relayPeerId });
  };

  // Remove from relay history（仅本会话内存）
  const handleRemoveRelayHistory = (id: string) => {
    setRelayHistory(prev => prev.filter(h => h.id !== id));
  };

  // Select from relay history
  const handleSelectRelayHistory = (item: RelayHistoryItem) => {
    setRelayServerUrl(item.serverUrl);
    setRelayPeerId(item.peerId);
    setShowRelayDropdown(false);
  };

  // Update progress based on state
  useEffect(() => {
    switch (state) {
      case ConnectionState.CONNECTING:
        setProgress(10);
        break;
      case ConnectionState.SIGNALING:
        setProgress(30);
        break;
      case ConnectionState.ICE_GATHERING:
        setProgress(50);
        break;
      case ConnectionState.ICE_CONNECTING:
        setProgress(75);
        break;
      case ConnectionState.CONNECTED:
        setProgress(100);
        break;
      case ConnectionState.RECONNECTING:
        setProgress(20 + (reconnectAttempt / maxAttempts) * 40);
        break;
      default:
        setProgress(0);
    }
  }, [state, reconnectAttempt, maxAttempts]);

  const isConnecting = [
    ConnectionState.CONNECTING,
    ConnectionState.SIGNALING,
    ConnectionState.ICE_GATHERING,
    ConnectionState.ICE_CONNECTING,
    ConnectionState.RECONNECTING,
  ].includes(state);

  const stateInfo = CONNECTION_STATE_INFO[state] || { label: state, description: '' };

  return (
    <div className={`fixed inset-0 z-[100] ${frozenFrameUrl ? '' : 'bg-[#050505]'} flex flex-col items-center justify-center p-8 transition-opacity duration-700 font-sans`}>
      {/* 冻结最后一帧作为背景（断线重连期间无黑屏），毛玻璃虚化 + 加深遮罩 */}
      {frozenFrameUrl && (
        <>
          <img
            src={frozenFrameUrl}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            style={{ filter: 'blur(8px) brightness(0.4)' }}
          />
          <div className="absolute inset-0 bg-black/50" />
        </>
      )}
      {/* Background Ambience */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] bg-blue-600/10 blur-[150px] rounded-full" />
        <div className="absolute -bottom-[20%] -right-[10%] w-[50%] h-[50%] bg-purple-600/5 blur-[150px] rounded-full" />
      </div>

      <div className="relative max-w-lg w-full bg-[#0a0a0c] border border-white/10 rounded-3xl p-10 shadow-2xl flex flex-col items-center text-center space-y-8">
        {/* Connecting State */}
        {isConnecting && (
          <>
            <div className="relative w-20 h-20">
              <div className="absolute inset-0 border-4 border-white/5 rounded-full" />
              <div className="absolute inset-0 border-4 border-blue-500 border-t-transparent border-l-transparent rounded-full animate-spin" />
              <div className="absolute inset-0 flex items-center justify-center">
                <RefreshCw className="text-blue-500 animate-pulse" size={24} />
              </div>
            </div>

            <div className="space-y-2 w-full">
              <h2 className="text-2xl font-bold text-white tracking-tight">
                {hostWaitingInfo ? '等待主机上线' : stateInfo.label}
              </h2>
              <p className="text-zinc-400 text-sm font-mono">
                {hostWaitingInfo
                  ? (hostWaitingInfo.maxAttempts > 0
                      ? `主机未上线，正在等待... ${hostWaitingInfo.maxAttempts - hostWaitingInfo.attempt + 1}秒`
                      : '主机未上线，等待中...')
                  : stateInfo.description}
                {!hostWaitingInfo && state === ConnectionState.RECONNECTING && ` (${reconnectAttempt}/${maxAttempts})`}
              </p>
              {!hostWaitingInfo && signalingRetryAttempt !== null && signalingRetryAttempt > 0 && (
                <p className="text-zinc-500 text-xs font-mono mt-1">
                  信令重连中（已尝试 {signalingRetryAttempt} 次）
                </p>
              )}
            </div>

            <div className="w-full space-y-3">
              <div className="relative h-1.5 bg-white/10 rounded-full overflow-hidden">
                <div
                  className="absolute top-0 left-0 h-full bg-blue-500 transition-all duration-700 ease-out"
                  style={{ width: `${progress}%` }}
                />
                <div
                  className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow-[0_0_10px_rgba(255,255,255,0.8)] transition-all duration-700 ease-out"
                  style={{ left: `calc(${progress}% - 6px)` }}
                />
              </div>
              <div className="flex justify-between text-[10px] font-mono text-zinc-500 uppercase tracking-widest">
                <span>{stateInfo.label}</span>
                <span>{Math.round(progress)}%</span>
              </div>
            </div>

            <div className="flex gap-3 mt-4">
              <button
                onClick={() => setShowRacingDetails(!showRacingDetails)}
                className="py-2.5 px-4 rounded-xl bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 hover:text-blue-300 text-sm font-medium transition-colors border border-blue-500/20 flex items-center gap-2"
              >
                <Info size={16} /> {showRacingDetails ? '隐藏详情' : '详情'}
              </button>
              <button
                onClick={onCancel}
                className="py-2.5 px-6 rounded-xl bg-white/5 hover:bg-white/10 text-zinc-400 hover:text-white text-sm font-medium transition-colors border border-white/5 flex items-center gap-2"
              >
                <XCircle size={16} /> 取消连接
              </button>
            </div>

            {/* ICE Racing Details Panel */}
            {showRacingDetails && (
              <div className="w-full mt-4">
                <IceRacingPanel onClose={() => setShowRacingDetails(false)} />
              </div>
            )}
          </>
        )}

        {/* Failed State */}
        {state === ConnectionState.FAILED && (
          <>
            <div className="w-20 h-20 bg-red-500/10 rounded-full flex items-center justify-center border border-red-500/20">
              <AlertTriangle className="text-red-500" size={36} />
            </div>

            <div className="space-y-2">
              <h2 className="text-2xl font-bold text-white tracking-tight">连接失败</h2>
              <p className="text-red-400 text-sm font-mono">
                无法连接到主机
              </p>
            </div>

            <div className="w-full bg-red-500/5 border border-red-500/10 rounded-xl p-5 text-left space-y-3">
              <h3 className="text-xs font-bold text-zinc-400 uppercase tracking-widest">可能的原因</h3>
              <ul className="space-y-2 text-sm text-zinc-300">
                <li className="flex gap-2 items-center">
                  <span className="w-1 h-1 bg-red-500 rounded-full" />
                  主机不在线或 Peer ID 不正确
                </li>
                <li className="flex gap-2 items-center">
                  <span className="w-1 h-1 bg-red-500 rounded-full" />
                  网络不稳定或响应超时
                </li>
                <li className="flex gap-2 items-center">
                  <span className="w-1 h-1 bg-red-500 rounded-full" />
                  中转服务器连接失败
                </li>
              </ul>
            </div>

            <div className="flex gap-3 w-full">
              <button
                onClick={() => onConnectRelay({ serverUrl: relayServerUrl, peerId: relayPeerId })}
                disabled={!relayPeerId.trim()}
                className="flex-1 py-3 bg-white text-black font-bold rounded-xl hover:bg-zinc-200 disabled:bg-zinc-700 disabled:text-zinc-500 transition-colors shadow-lg flex items-center justify-center gap-2"
              >
                <RefreshCw size={16} /> 重试连接
              </button>
              <button
                onClick={() => setShowLogs(!showLogs)}
                className="flex-1 py-3 bg-white/5 hover:bg-white/10 text-zinc-300 hover:text-white font-bold rounded-xl transition-colors border border-white/5 flex items-center justify-center gap-2"
              >
                <Terminal size={16} /> {showLogs ? '隐藏诊断' : '查看诊断'}
              </button>
            </div>
          </>
        )}

        {/* Decrypting State */}
        {state === ConnectionState.DISCONNECTED && isDecrypting && (
          <>
            <div className="w-20 h-20 bg-purple-500/10 rounded-full flex items-center justify-center border border-purple-500/20">
              <Loader2 className="text-purple-500 animate-spin" size={36} />
            </div>

            <div className="space-y-2">
              <h2 className="text-2xl font-bold text-white tracking-tight">正在验证连接</h2>
              <p className="text-zinc-400 text-sm font-mono">
                解密连接参数中...
              </p>
            </div>
          </>
        )}

        {/* Decrypt Error State */}
        {state === ConnectionState.DISCONNECTED && decryptError && !isDecrypting && (
          <>
            <div className="w-20 h-20 bg-red-500/10 rounded-full flex items-center justify-center border border-red-500/20">
              <AlertTriangle className="text-red-500" size={36} />
            </div>

            <div className="space-y-2">
              <h2 className="text-2xl font-bold text-white tracking-tight">连接验证失败</h2>
              <p className="text-red-400 text-sm font-mono">
                {decryptError}
              </p>
            </div>

            <div className="w-full bg-red-500/5 border border-red-500/10 rounded-xl p-5 text-left space-y-3">
              <h3 className="text-xs font-bold text-zinc-400 uppercase tracking-widest">可能的原因</h3>
              <ul className="space-y-2 text-sm text-zinc-300">
                <li className="flex gap-2 items-center">
                  <span className="w-1 h-1 bg-red-500 rounded-full" />
                  链接已过期或被篡改
                </li>
                <li className="flex gap-2 items-center">
                  <span className="w-1 h-1 bg-red-500 rounded-full" />
                  浏览器指纹不匹配（换了设备或浏览器）
                </li>
                <li className="flex gap-2 items-center">
                  <span className="w-1 h-1 bg-red-500 rounded-full" />
                  请联系管理员获取新的连接链接
                </li>
              </ul>
            </div>

            <button
              onClick={() => {
                setDecryptError(null);
                setConnectionMode('relay');
              }}
              className="w-full py-3 bg-white/5 hover:bg-white/10 text-zinc-300 hover:text-white font-bold rounded-xl transition-colors border border-white/5 flex items-center justify-center gap-2"
            >
              手动输入连接
            </button>
          </>
        )}

        {/* Disconnected State */}
        {state === ConnectionState.DISCONNECTED && !isDecrypting && !decryptError && (
          <>
            <div className="w-24 h-24 bg-gradient-to-br from-blue-600 to-blue-800 rounded-3xl flex items-center justify-center shadow-2xl shadow-blue-900/20 rotate-3 hover:rotate-6 transition-transform duration-500">
              <Power className="text-white" size={40} />
            </div>

            <div className="space-y-4">
              <h1 className="text-4xl font-bold text-white tracking-tighter">
                WebRTC<span className="text-blue-500">Go</span>
              </h1>
              <p className="text-zinc-400 text-sm max-w-xs mx-auto leading-relaxed">
                基于 WebRTC 的下一代高性能远程桌面协议。
                <br />
                <span className="text-zinc-600">低延迟 · 4K 60FPS · 硬件加速</span>
              </p>
            </div>

            {/* Peer ID Input */}
            <div className="w-full space-y-3">
              {/* History Dropdown */}
              {relayHistory.length > 0 && (
                <div className="relative">
                  <button
                    onClick={() => setShowRelayDropdown(!showRelayDropdown)}
                    className="w-full py-3 px-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-left flex items-center justify-between transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <Radio size={18} className="text-purple-400" />
                      <div>
                        <div className="text-sm font-medium text-white">
                          {relayPeerId || '选择历史连接'}
                        </div>
                        <div className="text-[10px] text-zinc-500 font-mono">{relayServerUrl}</div>
                      </div>
                    </div>
                    <ChevronDown size={16} className={`text-zinc-400 transition-transform ${showRelayDropdown ? 'rotate-180' : ''}`} />
                  </button>

                  {showRelayDropdown && (
                    <div className="absolute top-full left-0 right-0 mt-2 bg-[#0c0c0e] border border-white/10 rounded-xl z-10 animate-fade-in shadow-2xl max-h-40 overflow-y-auto">
                      {relayHistory.map((item) => (
                        <div
                          key={item.id}
                          className={`flex items-center justify-between px-4 py-2.5 hover:bg-white/5 cursor-pointer transition-colors ${relayPeerId === item.peerId ? 'bg-purple-500/10' : ''}`}
                          onClick={() => handleSelectRelayHistory(item)}
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <Radio size={14} className={relayPeerId === item.peerId ? 'text-purple-400' : 'text-zinc-500'} />
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-white truncate">{item.peerId}</div>
                              <div className="text-[10px] text-zinc-500 font-mono truncate">{item.serverUrl}</div>
                            </div>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveRelayHistory(item.id);
                            }}
                            className="p-1.5 hover:bg-red-500/20 rounded-lg transition-colors flex-shrink-0"
                          >
                            <Trash2 size={14} className="text-red-400" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-3">
                <div className="text-xs font-bold text-zinc-400 uppercase tracking-widest flex items-center gap-2">
                  <Radio size={12} className="text-purple-400" />
                  {relayHistory.length > 0 ? '编辑连接' : '中转服务器设置'}
                </div>

                <div className="space-y-2">
                  <input
                    type="text"
                    placeholder="中转服务器地址"
                    value={relayServerUrl}
                    onChange={(e) => setRelayServerUrl(e.target.value)}
                    className="w-full px-3 py-2 bg-black/50 border border-white/10 rounded-lg text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-purple-500/50"
                  />
                  <input
                    type="text"
                    placeholder="Peer ID (主机端配置的ID)"
                    value={relayPeerId}
                    onChange={(e) => setRelayPeerId(e.target.value)}
                    className="w-full px-3 py-2 bg-black/50 border border-white/10 rounded-lg text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-purple-500/50"
                  />
                </div>

                <div className="text-[10px] text-zinc-600">
                  通过中转服务器连接，连接成功后自动保存到历史记录
                </div>
              </div>
            </div>

            {/* Connect Button */}
            <button
              onClick={handleRelayConnect}
              disabled={!relayPeerId.trim()}
              className="w-full py-4 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 disabled:from-zinc-700 disabled:to-zinc-700 disabled:text-zinc-500 text-white font-bold rounded-2xl transition-all shadow-[0_0_20px_rgba(168,85,247,0.2)] hover:shadow-[0_0_30px_rgba(168,85,247,0.3)] hover:scale-[1.02] flex items-center justify-center gap-2 group"
            >
              <Radio size={18} className="group-hover:translate-x-0.5 transition-transform" />
              连接
            </button>
          </>
        )}

        {/* Diagnostic Logs Panel */}
        {showLogs && (
          <div className="w-full h-48 bg-black/80 border border-white/10 rounded-xl p-3 overflow-hidden flex flex-col animate-fade-in">
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center gap-2 text-[10px] font-bold text-zinc-500 uppercase">
                <Terminal size={12} /> Console Output
              </div>
              <button onClick={() => setShowLogs(false)} className="text-zinc-500 hover:text-white">
                <XCircle size={12} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto font-mono text-[10px] space-y-1 text-left">
              {logs.map((log) => (
                <div key={log.id} className="flex gap-2">
                  <span className="text-zinc-600">
                    [{log.timestamp.toLocaleTimeString([], { hour12: false, minute: '2-digit', second: '2-digit' })}]
                  </span>
                  <span
                    className={`
                      ${log.level === 'error' ? 'text-red-400' : ''}
                      ${log.level === 'warn' ? 'text-yellow-400' : ''}
                      ${log.level === 'success' ? 'text-emerald-400' : ''}
                      ${log.level === 'info' ? 'text-zinc-400' : ''}
                    `}
                  >
                    {log.message}
                  </span>
                </div>
              ))}
              <div ref={logEndRef} />
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      
    </div>
  );
};

export default React.memo(ConnectionOverlay);


